import {
    DashboardFolderT,
    DashboardFolderTreeNodeT,
    DashboardT,
    FolderTreeT,
} from 'business/definitions';

/**
 * Recursively prunes empty folders from the folder tree.
 * A folder is considered empty if it has no dashboards or no subfolders.
 */
export const pruneEmptyFolders = (
    folder: DashboardFolderTreeNodeT,
): boolean => {
    folder.folders = folder.folders.filter((f) => pruneEmptyFolders(f));
    return Boolean(folder.dashboards.length || folder.folders.length);
};

/**
 * Assigns dashboards to their respective folders based on the folder ID.
 */
export const assignDashboardsToFolders = (
    foldersDict: { [key: number]: DashboardFolderTreeNodeT },
    dashboards: DashboardT[],
) => {
    for (const dashboard of dashboards) {
        if (dashboard.folder && foldersDict[dashboard.folder.id]) {
            foldersDict[dashboard.folder.id].dashboards.push(dashboard);
        }
    }
};

/**
 * Instantiates root folders from the dashboard folders.
 * Each folder is added to its parent folder if it has one,
 * otherwise it is added to the root folders array.
 * Optionally prevents a folder from being added to the tree.
 */
export const instantiateRootFolders = (
    dashboardFolders: DashboardFolderT[],
    foldersDict: { [key: number]: DashboardFolderTreeNodeT },
    excludedFolderId?: number | null,
): DashboardFolderTreeNodeT[] => {
    const rootFolders: DashboardFolderTreeNodeT[] = [];
    for (const folder of dashboardFolders) {
        const currentFolder = foldersDict[folder.id];
        if (excludedFolderId && folder.id === excludedFolderId) continue;
        if (
            folder.parentFolder !== null &&
            foldersDict[folder.parentFolder.id]
        ) {
            const parent = foldersDict[folder.parentFolder.id];
            parent.folders.push(currentFolder);
        } else {
            rootFolders.push(currentFolder);
        }
    }
    return rootFolders;
};

/**
 * Builds a folder tree structure including the provided dashboards.
 */
export const buildDashboardFolderTree = (
    dashboardFolders: DashboardFolderT[],
    dashboards: DashboardT[] | null,
    isAdmin: boolean,
    excludedFolderId?: number | null,
): FolderTreeT => {
    const foldersDict: { [key: number]: DashboardFolderTreeNodeT } = {};
    for (const folder of dashboardFolders) {
        foldersDict[folder.id] = {
            id: folder.id,
            name: folder.name,
            parentFolder: folder.parentFolder,
            dashboards: [],
            folders: [],
        };
    }

    if (dashboards) assignDashboardsToFolders(foldersDict, dashboards);

    let rootFolders = instantiateRootFolders(
        dashboardFolders,
        foldersDict,
        excludedFolderId,
    );
    if (!isAdmin) rootFolders = rootFolders.filter((f) => pruneEmptyFolders(f));

    if (dashboards)
        return {
            dashboards: dashboards.filter((d) => d.folder === null),
            folders: rootFolders,
        };
    return { dashboards: [], folders: rootFolders };
};

/**
 * Finds a folder in the tree by its ID. The id comes as a string from the URL params.
 */
export const findFolderInTreeById = (
    tree: DashboardFolderTreeNodeT[],
    folderId: string | null,
): DashboardFolderTreeNodeT | null => {
    if (!folderId) return null;
    for (const folder of tree) {
        if (folder.id.toString() === folderId) {
            return folder;
        }

        if (folder.folders?.length) {
            const found = findFolderInTreeById(folder.folders, folderId);
            if (found) {
                return found;
            }
        }
    }
    return null;
};
